import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  cancelEnrollment,
  createDripSequence,
  deleteDripSequence,
  enrollInDrip,
  listDripSequences,
  listEnrollments,
  updateDripSequence,
  type DripSequenceInput,
  type EnrollInput,
} from '@/api/drip'

export function useDripSequences() {
  return useQuery({ queryKey: ['drip-sequences'], queryFn: listDripSequences })
}

export function useCreateDripSequence() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: DripSequenceInput) => createDripSequence(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drip-sequences'] }),
  })
}

export function useUpdateDripSequence() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: DripSequenceInput }) => updateDripSequence(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drip-sequences'] }),
  })
}

export function useDeleteDripSequence() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteDripSequence(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drip-sequences'] }),
  })
}

export function useEnrollInDrip() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: EnrollInput }) => enrollInDrip(id, input),
    // ['drip-sequences'] also covers the enrolment lists; a list's "Used in" changes too
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['drip-sequences'] })
      qc.invalidateQueries({ queryKey: ['lists'] })
    },
  })
}

export function useDripEnrollments(sequenceId?: string) {
  return useQuery({
    queryKey: ['drip-sequences', sequenceId, 'enrollments'],
    queryFn: () => listEnrollments(sequenceId as string),
    enabled: !!sequenceId,
  })
}

export function useCancelEnrollment(sequenceId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (enrollmentId: string) => cancelEnrollment(sequenceId, enrollmentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drip-sequences'] }),
  })
}
